import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import StyledIconBox from '../../components/styledComponents/StyledIconBox';
import StyledText from '../../components/styledComponents/StyledText';
import StyledButton from '../../components/styledComponents/StyledButton';
import BoostFloralLogo from '../../components/svgIcons/BoostFloralLogo';
import CustomerIcon from '../../components/svgIcons/CustomerIcon';
import DashboardIcon from '../../components/svgIcons/DashboardIcon';
import ProductsIcon from '../../components/svgIcons/ProductsIcon';
import RightArrowNav from '../../components/svgIcons/RightArrowNav';
import SettingIcon from '../../components/svgIcons/SettingIcon';
import VendorIcon from '../../components/svgIcons/VendorIcon';
import './_sidebar.scss';
import WorldIcon from '../../components/svgIcons/WorldIcon';
import ImagesIcon from '../../components/svgIcons/ImagesIcon';
import CrossIconNew from '../../components/svgIcons/CrossIconNew';
import MenuMoreIcon from '../../components/svgIcons/MenuMoreIcon';
import EllipseIcons from '../../components/svgIcons/EllipseIcons';
import DocumentIcon from '../../components/svgIcons/DocumentIcon';
import { useGetUserDetailsQuery } from '../../../features/profile/profileApiSlice';
import { useDispatch } from 'react-redux';
import { openHelpSection } from '../../services/helpSectionSlice';
import { HELPSECTIONTOPICS } from '../../../utils/Constants';
import SupportIcon from '../../components/svgIcons/Support';
import { useSelector } from 'react-redux';
import { OverlayTrigger } from 'react-bootstrap';
import { StyledTooltip } from '../../components/styledComponents/StyledTooltip';
import { isAuthorized, isDisabled } from '../../../utils/helper';
import OrderOnDeliveryIcon from '../../components/svgIcons/OrderOnDeliveryIcon';
import OrderDesignedIcon from '../../components/svgIcons/OrderDesignedIcon';

const resolveMenuList = (userDetails, isAllowedViewDelivery) => {
  return [
    {
      name: 'Admin',
      icon: <DashboardIcon />,
      allowedRoles: ['superAdmin'],
      childList: [
        {
          name: 'Dashboard',
          to: 'admin-dashboard',
        },
        {
          name: 'Florist Orders',
          to: '/florist-orders',
        },
        {
          name: 'Demo Inquires',
          to: '/demo-inquires',
        },
        {
          name: 'Commission Insights',
          to: '/commission-insights',
        },
      ],
    },
    {
      name: 'Dashboard',
      icon: <DashboardIcon />,
      to: 'dashboard',
    },
    {
      name: 'Catalog',
      icon: <ProductsIcon />,
      to: 'products',
      allowedRoles: ['superAdmin'],
    },
    {
      name: 'Orders',
      icon: <OrderDesignedIcon />,
      to: 'orders',
    },
    {
      name: 'Deliveries',
      icon: <OrderOnDeliveryIcon size={26} />,
      to: 'deliveries',
    },
    {
      name: 'Florists',
      icon: <VendorIcon />,
      to: 'admin/florists',
    },
    {
      name: 'Images Gallery',
      icon: <ImagesIcon />,
      to: 'admin/images-gallery',
      allowedRoles: ['superAdmin'],
    },
    {
      name: 'Products',
      icon: <ProductsIcon />,
      to: '',
      childList: [
        {
          name: 'Your Products',
          to: '/settings/products',
        },
        {
          name: 'Addons',
          to: 'settings/addons',
        },
        {
          name: 'Catalog',
          to: '/settings/products/catalog',
        },
        {
          name: 'Product Settings',
          to: '/settings/product/settings',
        },
        {
          name: 'Inventory Insights',
          to: '/settings/inventory/Insights',
        },
        {
          name: 'Catalog Insights',
          to: '/settings/catalog/insights',
        },
      ],
    },

    {
      name: 'Your Shop',
      icon: <SettingIcon />,
      to: '',
      childList: [
        {
          name: 'Shop Information',
          to: 'shopInfo/details',
        },
        {
          name: 'Financials',
          to: 'shopInfo/financials',
        },
        {
          name: 'Shop Settings',
          to: 'shopInfo/orderSettings',
        },
        {
          name: 'Regional Settings',
          to: 'shopInfo/regionalSettings',
        },
        {
          name: 'Policy',
          to: 'shopInfo/policy',
        },
        {
          name: 'Integrations',
          to: 'shopInfo/integrations',
        },
        {
          name: 'Role',
          to: 'shopInfo/role',
          // allowedRoleIds: [2],
        },
        {
          name: 'Users',
          to: 'shopInfo/users',
          // allowedRoleIds: [2],
        },
        {
          name: 'Billing',
          icon: <DocumentIcon />,
          to: 'billing',
        },
      ],
    },
    {
      name: 'Customers',
      icon: <CustomerIcon />,
      to: 'customers',
    },
    {
      name: 'Website',
      icon: <WorldIcon />,
      to: '',
      childList: [
        {
          name: userDetails?.floristDTO?.usingWebHosting
            ? 'Edit your website'
            : 'Create your website',
          to: 'settings/createwebsite',
        },
        {
          name: 'Gallery',
          to: 'settings/gallery-settings',
        },
        {
          name: 'Inquiries',
          to: 'settings/inquiries',
          // allowedRoles: ['admin'],
        },
        {
          name: 'Reports',
          to: 'reports',
        },
      ],
    },
  ];
};

const SideBar = ({
  openSideMenu,
  expandMenu,
  setExpandMenu,
  setOpenSideMenu,
  isMobile,
  onboardingCompleted,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userPermissions = useSelector(
    (state) => state?.authorization?.userPermissions,
  );

  const has_permission_to_view_delivery_order = isAuthorized(
    userPermissions,
    'can_view_deliveries',
  );

  const { data: userDetails } = useGetUserDetailsQuery(null, {
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
    refetchOnFocus: true,
  });
  const [isMobileScreen, setIsMobileScreen] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const checkScreenSize = () => {
        setIsMobileScreen(window.innerWidth < 768);
      };

      checkScreenSize();
      window.addEventListener('resize', checkScreenSize);

      return () => {
        window.removeEventListener('resize', checkScreenSize);
      };
    }
  }, []);

  const getRotateValue = () =>
    openSideMenu ? (isMobile ? 'right' : 'left') : isMobile ? 'left' : 'right';

  const handleOpenHelpSection = (title) => {
    dispatch(
      openHelpSection({
        show: true,
        comingFrom: title,
      }),
    );
  };

  const sideBarMenu = useMemo(() => {
    const resolvedList = resolveMenuList(
      userDetails,
      has_permission_to_view_delivery_order,
    );
    return resolvedList
      .filter((item) => {
        // Check if the item is disabled first
        if (isDisabled(userPermissions, item?.to, userDetails?.roles)) {
          return false; // Exclude disabled items
        }

        // If only allowedRoleIds is present, check that and ignore allowedRoles
        if (item.allowedRoleIds && !item.allowedRoles) {
          return item.allowedRoleIds.some((roleId) => {
            return userDetails?.roles?.some(
              (userRole) => userRole.id === roleId,
            );
          });
        }

        // If only allowedRoles is present, check that and ignore allowedRoleIds
        if (item.allowedRoles && !item.allowedRoleIds) {
          return item.allowedRoles.some((role) => userDetails?.[role]);
        }

        // If both allowedRoleIds and allowedRoles are present, check both
        if (item.allowedRoleIds && item.allowedRoles) {
          const allowedRolesIdsMatch = item.allowedRoleIds.some((roleId) => {
            return userDetails?.roles?.some(
              (userRole) => userRole.id === roleId,
            );
          });

          const allowedRolesMatch = item.allowedRoles.some(
            (role) => userDetails?.[role],
          );

          return allowedRolesIdsMatch && allowedRolesMatch; // Require both to match
        }

        // If neither allowedRoleIds nor allowedRoles are present, return true by default
        return true;
      })
      .map((item) => {
        if (item.childList) {
          item.childList = item.childList.filter((childItem) => {
            // Check if the item is disabled first
            if (
              isDisabled(userPermissions, childItem?.to, userDetails?.roles)
            ) {
              return false; // Exclude disabled items
            }

            // Same logic for filtering child items
            if (childItem.allowedRoleIds && !childItem.allowedRoles) {
              return childItem.allowedRoleIds.some((roleId) => {
                return userDetails?.roles?.some(
                  (userRole) => userRole.id === roleId,
                );
              });
            }

            if (childItem.allowedRoles && !childItem.allowedRoleIds) {
              return childItem.allowedRoles.some((role) => userDetails?.[role]);
            }

            if (childItem.allowedRoleIds && childItem.allowedRoles) {
              const allowedRolesIdsMatch = childItem.allowedRoleIds.some(
                (roleId) => {
                  return userDetails?.roles?.some(
                    (userRole) => userRole.id === roleId,
                  );
                },
              );

              const allowedRolesMatch = childItem.allowedRoles.some(
                (role) => userDetails?.[role],
              );

              return allowedRolesIdsMatch && allowedRolesMatch; // Require both to match
            }

            return true; // Default to true if neither are present
          });
        }
        return item;
      });
  }, [userDetails, onboardingCompleted, userPermissions]);

  return (
    <>
      <StyledSideBar
        style={{ maxWidth: '350px' }}
        className={`${
          openSideMenu
            ? 'openSidebar'
            : isMobile
            ? 'closeSidebarMobile'
            : 'closeSidebar'
        } vh-100 p-0 position-fixed`}
      >
        <StyledSideMenu className={isMobile ? 'overflow-hidden' : ''}>
          {/* Open and close sidebar */}
          <div
            className={`sidebar-logo d-flex align-items-center justify-content-between menu-transition ${
              openSideMenu ? 'menu-open' : 'menu-closed'
            }`}
          >
            <span
              className={`${
                !openSideMenu && !isMobile ? 'd-none' : 'd-block'
              } `}
            >
              <BoostFloralLogo
                size="182px"
                className={`me-2 me-md-4 menu-logo`}
              />
            </span>
            <span
              className={`cursor-pointer menu-opener sidebar-arrow ${
                isMobile ? 'mobile-sidebar-arrow' : ''
              } ${openSideMenu && isMobile ? 'mobile-close-sidebar' : ''}`}
              onClick={() => {
                setOpenSideMenu(!openSideMenu);
                setExpandMenu('');
              }}
            >
              {openSideMenu ? (
                <CrossIconNew />
              ) : (
                <RightArrowNav rotate={getRotateValue()} />
              )}
            </span>
          </div>
          <div className="menu_list">
            {sideBarMenu.map((item, index) => (
              <React.Fragment key={index}>
                {item?.childList && item?.childList?.length ? (
                  <React.Fragment key={index}>
                    {/* This contains Your shop, website and it is used to open the submenu */}
                    <CustomStyledText
                      onClick={() => {
                        if (openSideMenu) {
                          expandMenu === item.name.toLowerCase()
                            ? setExpandMenu('')
                            : setExpandMenu(item.name.toLowerCase());
                        }
                      }}
                      className={`d-flex align-items-center cursor-pointer mx-2 w-auto ${
                        openSideMenu
                          ? 'px-3 ms-2 p-2'
                          : 'px-3 p-2 justify-content-center'
                      } text-decoration-none mb-1 ${
                        expandMenu === item.name.toLowerCase() ? 'active' : ''
                      }`}
                    >
                      <span
                        className={`single_tab d-flex align-items-center w-100 `}
                      >
                        <CustomStyledIconBox
                          size="30px"
                          fill="none"
                          $marginRight={
                            openSideMenu && !isMobile ? '5px' : '0px'
                          }
                        >
                          {item.icon}
                        </CustomStyledIconBox>
                        {openSideMenu && (
                          <p className="menuText mb-0">{item.name}</p>
                        )}
                        {openSideMenu && (
                          <MenuMoreIcon
                            className="ms-auto"
                            open={`${
                              expandMenu === item.name.toLowerCase()
                                ? 'down'
                                : ''
                            }`}
                          />
                        )}
                        {/* When the side bar is close it will show submenu that time  */}
                        {item.childList && !openSideMenu && (
                          <div className="__dropwdown">
                            <div className="__dopdown-items">
                              {item.childList?.map((data, i) => {
                                return (
                                  <OverlayTrigger
                                    overlay={
                                      isDisabled(
                                        userPermissions,
                                        data?.to,
                                        userDetails?.roles,
                                      ) ? (
                                        <StyledTooltip>
                                          Unauthorized Access
                                        </StyledTooltip>
                                      ) : (
                                        <></>
                                      )
                                    }
                                  >
                                    <CustomStyledText
                                      key={`sub-menu-expand-menu-${i}`}
                                      to={
                                        !isDisabled(
                                          userPermissions,
                                          data?.to,
                                          userDetails?.roles,
                                        ) && data?.to
                                      }
                                      onClick={() => {
                                        if (
                                          !isDisabled(
                                            userPermissions,
                                            data?.to,
                                            userDetails?.roles,
                                          )
                                        ) {
                                          navigate(data.to); // Navigate to the `data.to` route
                                          if (isMobileScreen) {
                                            setOpenSideMenu(!openSideMenu);
                                          }
                                        }
                                      }}
                                      style={{
                                        cursor: !isDisabled(
                                          userPermissions,
                                          data?.to,
                                          userDetails?.roles,
                                        )
                                          ? 'pointer'
                                          : 'not-allowed',
                                        color:
                                          isDisabled(
                                            userPermissions,
                                            data?.to,
                                            userDetails?.roles,
                                          ) && '#bbb',
                                      }}
                                      className={`d-flex w-100 py-2 text-decoration-none sub-expand-menu ${
                                        location.pathname === data.to ||
                                        location.pathname === `/${data.to}`
                                          ? 'active'
                                          : ''
                                      }`}
                                    >
                                      <p
                                        className="mt-1 menuText mb-0"
                                        style={{
                                          cursor: !isDisabled(
                                            userPermissions,
                                            data?.to,
                                            userDetails?.roles,
                                          )
                                            ? 'pointer'
                                            : 'not-allowed',
                                        }}
                                      >
                                        {data.name}
                                      </p>
                                    </CustomStyledText>
                                  </OverlayTrigger>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </span>
                    </CustomStyledText>
                    {/* When side bar is open it will show submenu that time  */}
                    {expandMenu === item.name.toLowerCase()
                      ? item.childList?.map((data, i) => {
                          return (
                            <OverlayTrigger
                              overlay={
                                isDisabled(
                                  userPermissions,
                                  data?.to,
                                  userDetails?.roles,
                                ) ? (
                                  <StyledTooltip>
                                    Unauthorized Access
                                  </StyledTooltip>
                                ) : (
                                  <></>
                                )
                              }
                            >
                              <CustomStyledText
                                key={`sub-menu-expand-menu-${i}`}
                                to={
                                  !isDisabled(
                                    userPermissions,
                                    data?.to,
                                    userDetails?.roles,
                                  ) && data?.to
                                }
                                style={{
                                  cursor: !isDisabled(
                                    userPermissions,
                                    data?.to,
                                    userDetails?.roles,
                                  )
                                    ? 'pointer'
                                    : 'not-allowed',
                                  color:
                                    isDisabled(
                                      userPermissions,
                                      data?.to,
                                      userDetails?.roles,
                                    ) && '#bbb',
                                }}
                                isDisabled={isDisabled(
                                  userPermissions,
                                  data?.to,
                                  userDetails?.roles,
                                )}
                                onClick={() => {
                                  if (
                                    !isDisabled(
                                      userPermissions,
                                      data?.to,
                                      userDetails?.roles,
                                    )
                                  ) {
                                    navigate(data.to); // Navigate to the `data.to` route
                                    if (isMobileScreen) {
                                      setOpenSideMenu(!openSideMenu);
                                    }
                                  }
                                }}
                                className={`${
                                  openSideMenu
                                    ? 'align-items-center px-4 ms-4 '
                                    : 'justify-content-center py-3 '
                                } ${
                                  location.pathname === data.to ||
                                  location.pathname === `/${data.to}`
                                    ? 'active'
                                    : ''
                                } d-flex py-2 text-decoration-none sub-expand-menu`}
                              >
                                <EllipseIcons
                                  style={{
                                    cursor: !isDisabled(
                                      userPermissions,
                                      data?.to,
                                      userDetails?.roles,
                                    )
                                      ? 'pointer'
                                      : 'not-allowed',
                                    color:
                                      isDisabled(
                                        userPermissions,
                                        data?.to,
                                        userDetails?.roles,
                                      ) && '#d8d8d8',
                                    height:
                                      isDisabled(
                                        userPermissions,
                                        data?.to,
                                        userDetails?.roles,
                                      ) && '8px',
                                    width:
                                      isDisabled(
                                        userPermissions,
                                        data?.to,
                                        userDetails?.roles,
                                      ) && '8px',
                                  }}
                                />
                                <p
                                  className="ms-2 menuText mb-0"
                                  style={{
                                    cursor: !isDisabled(
                                      userPermissions,
                                      data?.to,
                                      userDetails?.roles,
                                    )
                                      ? 'pointer'
                                      : 'not-allowed',
                                  }}
                                >
                                  {data.name}
                                </p>
                              </CustomStyledText>
                            </OverlayTrigger>
                          );
                        })
                      : null}
                  </React.Fragment>
                ) : (
                  (!item.childList ||
                    (item.childList && item.childList.length > 0)) && (
                    <OverlayTrigger
                      overlay={
                        isDisabled(
                          userPermissions,
                          item?.to,
                          userDetails?.roles,
                        ) ? (
                          <StyledTooltip>Unauthorized Access</StyledTooltip>
                        ) : (
                          <></>
                        )
                      }
                    >
                      <CustomStyledText
                        key={item.to}
                        to={
                          !isDisabled(
                            userPermissions,
                            item?.to,
                            userDetails?.roles,
                          ) && item?.to
                        }
                        as={NavLink}
                        style={{
                          cursor: !isDisabled(
                            userPermissions,
                            item?.to,
                            userDetails?.roles,
                          )
                            ? 'pointer'
                            : 'not-allowed',
                          color:
                            isDisabled(
                              userPermissions,
                              item?.to,
                              userDetails?.roles,
                            ) && '#bbb',
                        }}
                        isDisabled={isDisabled(
                          userPermissions,
                          item?.to,
                          userDetails?.roles,
                        )}
                        opensidemenu={openSideMenu?.toString()}
                        className={`${
                          openSideMenu
                            ? 'd-flex align-items-center px-3 mx-2 p-2'
                            : 'justify-content-center d-flex p-2 '
                        } text-decoration-none mb-1`}
                        onClick={() => {
                          !isDisabled(
                            userPermissions,
                            item?.to,
                            userDetails?.roles,
                          ) && setExpandMenu('');
                          if (isMobileScreen) {
                            setOpenSideMenu(!openSideMenu);
                          }
                        }}
                      >
                        <CustomStyledIconBox
                          size="30px"
                          fill="none"
                          $marginRight={
                            openSideMenu && !isMobile ? '5px' : '0px'
                          }
                          style={{
                            cursor: !isDisabled(
                              userPermissions,
                              item?.to,
                              userDetails?.roles,
                            )
                              ? 'pointer'
                              : 'not-allowed',
                            color:
                              isDisabled(
                                userPermissions,
                                item?.to,
                                userDetails?.roles,
                              ) && '#AFB1B4',
                            background:
                              isDisabled(
                                userPermissions,
                                item?.to,
                                userDetails?.roles,
                              ) && 'transparent',
                          }}
                        >
                          {item.icon}
                        </CustomStyledIconBox>
                        {openSideMenu && (
                          <p
                            className="menuText mb-0"
                            style={{
                              cursor: !isDisabled(
                                userPermissions,
                                item?.to,
                                userDetails?.roles,
                              )
                                ? 'pointer'
                                : 'not-allowed',
                            }}
                          >
                            {item.name}
                          </p>
                        )}
                      </CustomStyledText>
                    </OverlayTrigger>
                  )
                )}
              </React.Fragment>
            ))}
          </div>
          <ContactButtonWrapper
            className="mx-3 mb-3 p-2 rounded shadow"
            opensidemenu={openSideMenu?.toString()}
          >
            <div className="desktop">
              <StyledText
                $fontSize="16px"
                $fontWeight="400"
                color="headingColor"
                className="text-center"
              >
                Hi, how can we help?
              </StyledText>
              <div className="d-flex justify-content-center">
                <StyledButton className="w-75" $paddingY="5px">
                  <StyledText
                    $fontSize="18px"
                    $fontWeight="500"
                    color="white"
                    className="text-center mb-0"
                    onClick={() =>
                      handleOpenHelpSection(HELPSECTIONTOPICS.CONTACTUS)
                    }
                  >
                    Contact Us
                  </StyledText>
                </StyledButton>
              </div>
            </div>
            <StyledButton textButton className="mobile" $paddingY="5px">
              <StyledText
                $fontSize="18px"
                $fontWeight="500"
                color="white"
                className="text-center mb-0"
                onClick={() =>
                  handleOpenHelpSection(HELPSECTIONTOPICS.CONTACTUS)
                }
              >
                <SupportIcon size={30} />
              </StyledText>
            </StyledButton>
          </ContactButtonWrapper>
        </StyledSideMenu>
      </StyledSideBar>
    </>
  );
};

const ContactButtonWrapper = styled.div`
  width: ${({ opensidemenu }) => (opensidemenu === 'true' ? 'auto' : '50px')};
  .desktop {
    display: ${({ opensidemenu }) =>
      opensidemenu === 'true' ? 'initial' : 'none'};
  }

  .mobile {
    display: ${({ opensidemenu }) =>
      opensidemenu === 'true' ? 'none' : 'block'};
  }
`;

const CustomStyledIconBox = styled(StyledIconBox)`
  border: none;
`;

const StyledSideBar = styled.div`
  z-index: 999;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.04);
  background-color: ${({ theme }) => theme.white};
  transition: all 0.5s ease-in-out;

  .sidebar-arrow {
    width: 32px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    @media screen and (max-width: 767px) {
      top: 20px;
    }
    &.mobile-sidebar-arrow {
      width: 26px;
      height: 26px;
    }

    ${({ opensidemenu }) => opensidemenu !== 'true' && 'margin-bottom: 3.4px;'};
    //&.mobile-close-sidebar {
    //  position: absolute;
    //  top: 30px;
    //  right: -30px;
    //  background-color: #fff;
    //  box-shadow: rgba(9, 30, 66, 0.08) 0 0 0 1px,
    //    rgba(9, 30, 66, 0.08) 0 2px 4px 1px;
    //  border-radius: 50%;
    //}
  }
`;

const CustomStyledText = styled.span`
  color: ${({ theme }) => theme.bodyMedium};
  ${({ opensidemenu }) =>
    opensidemenu !== 'true' && 'margin: auto;  width: fit-content;'};

  font-weight: bold;
  border-radius: 8px;
  // &:hover {
  //   color: ${({ theme }) => theme.placeholderColor};
  // }

  > div,
  .single_tab > div:first-child {
    color: ${({ theme }) => theme.dark40};
    background-color: ${({ theme }) => theme.transparent};
  }

  &.active,
  &:hover {
    color: ${({ theme, isDisabled }) => !isDisabled && theme.white};
    background-color: ${({ theme, isDisabled }) =>
      !isDisabled && theme.primary};

    > div,
    .single_tab > div:first-child {
      background-color: ${({ theme, isDisabled }) =>
        !isDisabled && theme.primary};
      color: ${({ theme, isDisabled }) => !isDisabled && theme.white};
    }

    // &:before {
    //   content: '';
    //   position: absolute;
    //   display: block;
    //   width: 4px;
    //   height: 36px;
    //   border-top-right-radius: 10px;
    //   border-bottom-right-radius: 10px;
    //   left: 0;
    //   background-color: ${({ theme }) => `${theme.primary}`};
    //   margin-bottom: 0;
    // }.
  }
  &.sub-expand-menu {
    margin: 5px 0;
    color: ${({ theme }) => theme.dark40};
    background-color: ${({ theme }) => theme.transparent};
    > svg {
      color: ${({ theme }) => theme.dark70};
    }
    &.active,
    &:hover {
      color: ${({ theme, isDisabled }) => !isDisabled && theme.primary};
      background-color: ${({ theme, isDisabled }) =>
        !isDisabled && theme.transparent};

      > div,
      .single_tab > div:first-child,
      > svg {
        width: 12px;
        height: 12px;
        background-color: ${({ theme, isDisabled }) =>
          !isDisabled && theme.transparent};
        color: ${({ theme, isDisabled }) => !isDisabled && theme.primary};
      }
    }
  }

  p {
    font-size: 16px;
    font-family: 'Instrument Sans';
    font-weight: 500;
  }

  .expended-color {
    color: ${({ theme }) => theme.primaryDark};
  }

  .single_tab {
    position: relative;
    justify-content: center;

    .__dropwdown {
      display: none;
    }

    &:hover {
      .__dropwdown {
        .__dopdown-items {
          box-shadow: 0 6px 14px rgba(0, 0, 0, 0.05);
          position: absolute;
          top: 0;
          left: 90px;
          z-index: 999999;

          a {
            color: ${({ theme, isDisabled }) =>
              !isDisabled && theme.placeholderColor};

            &.active,
            &:hover {
              color: ${({ theme, isDisabled }) => !isDisabled && theme.primary};
            }
          }

          background-color: ${({ theme, isDisabled }) =>
            !isDisabled && theme.white};
          width: auto;
          border: 0 !important;
          padding: 0.8rem 1.2rem !important;
          border-radius: 4px;

          &:after {
            z-index: 0;
            content: '';
            position: absolute;
            left: -15px;
            top: 17px;
            transform: rotate(272deg);
            width: 0;
            height: 0;
            border-style: solid;
            border-width: 0 10px 12px 10px;
            border-color: transparent transparent #ffffff transparent;
          }
        }

        display: block;
        position: fixed;
        left: 10px;
        z-index: 9999;
        width: 400px;
        margin-top: 1px;
        padding-inline: 40px;
        height: 44px;
      }
    }
  }
`;

const StyledSideMenu = styled.div`
  height: 100%;

  transition: all 0.5s ease-in;
  .sidebar-logo {
    width: 100%;
    border-bottom: 1.5px solid ${({ theme }) => theme.dark70};
    padding: 33px 20px;
    margin-bottom: 25px;
    transition: all 0.5s ease-in-out;
    svg {
      width: 180px;
    }
  }

  .menu_list {
    overflow-y: auto;
    height: calc(100vh - 225px);

    &::-webkit-scrollbar {
      width: 5px;
    }
    &::-webkit-scrollbar-button {
      display: none;
    }
    &::-webkit-scrollbar-track-piece {
      display: none;
    }
    &::-webkit-scrollbar-thumb {
      background: #ccc;
    }

    ${({ theme }) => css`
      @media (max-width: ${theme?.breakpoints?.md}px) {
        height: 70vh;
      }
    `}
  }
`;

export default SideBar;
